import { useTranslation } from '@/hooks/useTranslation';

import { SimpleInput } from '@/components/form/inputs/SimpleInput';
import { DropDownList } from '@/components/form/inputs/DropDownList';
import { MultipleCheckbox } from '@/components/form/inputs/MultipleCheckBoxes';

import { lubricationTypes } from '@/features/traceability/data/lubricationOptions';
import { clampingTypes } from '@/features/traceability/data/clampingOptions';
import { toolHolderTypes } from '@/features/traceability/data/toolHolderOptions';
import { toolState as toolStateOptions, toolChange } from '@/features/traceability/data/toolState';
import { SectionHeader, FieldRow, type SectionRefs } from './TraceEditPrimitives';
import { TraceEditGeneralSection } from './TraceEditGeneralSection';
import type { Material } from '@/features/traceability/type/Material';
import type { TraceabilityApiClient } from '@/features/traceability/api/TraceabilityApiClient';

type TraceEditSectionsProps = {
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
  refs: SectionRefs;
  materials: Material[];
  traceabilityApiClient: TraceabilityApiClient | null;
};

export function TraceEditSections({ values, onChange, refs, materials, traceabilityApiClient }: TraceEditSectionsProps) {
  const { t } = useTranslation();

  return (
    <>
      <TraceEditGeneralSection
        values={values}
        onChange={onChange}
        refs={refs}
        materials={materials}
        traceabilityApiClient={traceabilityApiClient}
      />

      {/* Lubrication */}
      <div>
        <SectionHeader title={t('toolView.advanced.sections.lubrication')} />
        <FieldRow>
          <DropDownList
            ref={refs.lubTypeRef}
            name="lubType"
            label={t('traceability.lubricationTab.typeLabel')}
            value={(values['lubType'] as string) ?? ''}
            onChange={(val) => onChange('lubType', val)}
            options={lubricationTypes.map(({ value, labelKey }) => ({
              value,
              label: t(labelKey),
            }))}
            required
            side="bottom"
          />
        </FieldRow>
        <FieldRow>
          <SimpleInput
            name="lubBrand"
            label={t('traceability.lubricationTab.brandLabel')}
            value={(values['lubBrand'] as string) ?? ''}
            onChange={(val) => onChange('lubBrand', val)}
            placeholder={t('traceability.lubricationTab.brandPlaceholder')}
          />
        </FieldRow>
      </div>

      {/* Clamping */}
      <div>
        <SectionHeader title={t('toolView.advanced.sections.clamping')} />
        <FieldRow>
          <DropDownList
            name="clampingType"
            label={t('traceability.clampingTab.typeLabel')}
            value={(values['clampingType'] as string) ?? ''}
            onChange={(val) => onChange('clampingType', val)}
            options={clampingTypes.map(({ value, labelKey }) => ({ value, label: t(labelKey) }))}
            side="bottom"
          />
        </FieldRow>
        <FieldRow>
          <SimpleInput
            name="clampingBrand"
            label={t('traceability.clampingTab.brandLabel')}
            value={(values['clampingBrand'] as string) ?? ''}
            onChange={(val) => onChange('clampingBrand', val)}
            placeholder={t('traceability.clampingTab.brandPlaceholder')}
          />
        </FieldRow>
      </div>

      {/* Tool holder */}
      <div>
        <SectionHeader title={t('toolView.advanced.sections.holder')} />
        <FieldRow>
          <DropDownList
            name="holderType"
            label={t('traceability.holderTab.typeLabel')}
            value={(values['holderType'] as string) ?? ''}
            onChange={(val) => onChange('holderType', val)}
            options={toolHolderTypes.map(({ value, labelKey }) => ({
              value,
              label: t(labelKey),
            }))}
            side="bottom"
          />
        </FieldRow>
        <FieldRow>
          <SimpleInput
            name="holderBrand"
            label={t('traceability.holderTab.brandLabel')}
            value={(values['holderBrand'] as string) ?? ''}
            onChange={(val) => onChange('holderBrand', val)}
            placeholder={t('traceability.holderTab.brandPlaceholder')}
          />
        </FieldRow>
      </div>

      {/* Break / State */}
      <div>
        <SectionHeader title={t('toolView.advanced.sections.breakState')} />
        <FieldRow>
          <DropDownList
            ref={refs.toolStateRef}
            name="toolState"
            label={t('traceability.breakTab.stateLabel')}
            value={(values['toolState'] as string) ?? ''}
            onChange={(val) => onChange('toolState', val)}
            options={toolStateOptions.map(({ value, labelKey }) => ({
              value,
              label: t(labelKey),
            }))}
            required
            side="bottom"
          />
        </FieldRow>
        <FieldRow>
          <MultipleCheckbox
            ref={refs.toolChangeRef}
            name="toolChange"
            label={t('traceability.breakTab.reasonLabel')}
            value={(values['toolChange'] as string[]) ?? []}
            onChange={(val) => onChange('toolChange', val)}
            options={toolChange.map(({ value, labelKey }) => ({ value, label: t(labelKey) }))}
            required
          />
        </FieldRow>
        <FieldRow>
          <SimpleInput
            name="commentary"
            label={t('traceability.breakTab.commentLabel')}
            value={(values['commentary'] as string) ?? ''}
            onChange={(val) => onChange('commentary', val)}
            placeholder={t('traceability.breakTab.commentPlaceholder')}
            textarea
          />
        </FieldRow>
      </div>
    </>
  );
}
